import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import { ApolloProviderV4 } from '~/apollo/client-v4';
import { Role } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import { type OutcropMiniModelsPageQuery } from '~/apollo/generated/v4/graphql';
import { RoleSecured } from '~/components/auth/RoleSecured';
import { Heading } from '~/components/common/Heading';
import { NotFound } from '~/components/common/NotFound';
import { PublishedContainer } from '~/components/common/published-container';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { useShowUnpublished } from '~/components/upload/supporting-object/use-show-unpublished';
import { useRouteParam } from '~/hooks/routing';
import {
  countUnpublishedItems,
  isPublished,
} from '~/utils/modules/supporting-object';

const OUTCROP_MINI_MODELS_PAGE = graphql(`
  query OutcropMiniModelsPage($id: ID!) {
    outcropGet(id: $id) {
      id
      miniModels {
        id
        name
        description
        url
        lifecycleStatus
        priority
      }
    }
  }
`);

export default function OutcropMiniModelsPage() {
  return (
    <ApolloProviderV4>
      <OutcropMiniModelsPageInner />
    </ApolloProviderV4>
  );
}

function OutcropMiniModelsPageInner() {
  const outcropId = useRouteParam('outcropId', parseInt);

  const { data, loading } = useQuery(OUTCROP_MINI_MODELS_PAGE, {
    variables: {
      id: outcropId,
    },
  });

  const outcrop = data?.outcropGet;

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  const miniModels = outcrop.miniModels
    .slice()
    .sort(R.ascend(mm => mm.priority ?? Infinity));

  return <MiniModelList miniModels={miniModels} />;
}

type Outcrop = NonNullable<OutcropMiniModelsPageQuery['outcropGet']>;
type MiniModel = Outcrop['miniModels'][number];

function MiniModelList({ miniModels }: { miniModels: MiniModel[] }) {
  const unpublishedCount = countUnpublishedItems(miniModels);
  const { showUnpublished, PublishedToggle } =
    useShowUnpublished(unpublishedCount);

  const items = miniModels
    .filter(mm => showUnpublished || isPublished(mm))
    .sort(R.ascend(mm => mm.priority ?? Infinity));

  return (
    <div className="space-y-4">
      <RoleSecured roles={[Role.RoleAdmin]}>
        <PublishedToggle />
      </RoleSecured>

      <div className="grid lg:grid-cols-2 gap-4">
        {items.map(mm => (
          <PublishedContainer key={mm.id} published={isPublished(mm)}>
            <div className="w-full aspect-square thumbnail">
              <iframe title={mm.name} src={mm.url} className="w-full h-full" />
              <div className="caption text-left">
                <Heading level={5}>{mm.name}</Heading>
                {mm.description}
              </div>
            </div>
          </PublishedContainer>
        ))}
      </div>
    </div>
  );
}
